/**
 * Grand Quack Auto — pedestrian / NPC entity factory.
 *
 * Uses GLTF models from Poly Pizza (CC0) for visual variety.
 * Falls back to cylinder+sphere primitives until models load.
 * Brainrot meme names floating above each NPC.
 *
 * See research.md §3 "NPC system: state machine, not pathfinding".
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import type { World } from '../types';
import { sidewalkPaths } from '../world/cityGenerator';

export type NpcState = 'walking' | 'idle' | 'fleeing' | 'ragdoll';

export interface PedestrianEntity {
  group: THREE.Group;
  state: NpcState;
  pathIndex: number;
  waypointIndex: number;
  stateTimer: number;
  fleeDirection: THREE.Vector3;
  // ── Ragdoll fields (Phase B2 — mega-quack launch). Reused across launches. ─
  /** Horizontal (XZ) ragdoll velocity in m/s. y is unused. */
  ragdollHorizVel: THREE.Vector3;
  /** Vertical velocity in m/s — integrated against NPC.ragdollGravity. */
  ragdollVelY: number;
  /** Per-axis angular velocity in rad/s (the slapstick tumble). */
  ragdollAngVel: THREE.Vector3;
  /** Has the NPC already used its one ground bounce this launch? */
  ragdollBounced: boolean;
}

// ── NPC model loading ─────────────────────────────────────────────────────

const NPC_MODELS = ['/models/npc-man.glb', '/models/npc-woman1.glb', '/models/npc-woman2.glb'];
const loadedModels: THREE.Group[] = [];
const loader = new GLTFLoader();
let modelsLoading = false;

function preloadNpcModels(): void {
  if (modelsLoading) return;
  modelsLoading = true;
  for (const path of NPC_MODELS) {
    loader.load(path, (gltf) => {
      const model = gltf.scene;
      model.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          (child as THREE.Mesh).castShadow = true;
        }
      });
      loadedModels.push(model);
    });
  }
}

// Pastel colors for primitive fallback.
const NPC_COLORS = [0xcc9999, 0x99cccc, 0xcc99cc, 0x99cc99, 0xcccc99, 0x9999cc];

// Italian Brainrot meme names — the biggest meme trend of 2025.
const BRAINROT_NAMES = [
  'Quackuccini Duckolombo', 'Bombardino Quackadilo', 'Tralalero Rubberduckini',
  'Chimpanzini Pedantrini', 'Ducktor Spaghettini', 'Brembollini Quackzarelli',
  'Quackolini Macaronni', 'Tung Tung Sahur', 'La Vaca Saturno Quackarina',
  'Glorbo McQuackface', 'Sergio Quackamole', 'Don Featherlini',
  'Quackaroni Supremo', 'Pastafazool Duckworth', 'Count Quackula',
  'Big Quackowski', 'Señor Pato Loco', 'Waddles McHonkface',
  'Duckminster Fuller', 'Quacken Unleashed', 'El Pato Magnifico',
  'Sir Honks-a-Ton', 'Breadcrumb Bandit', 'Agent Double-O-Duck',
];

export function createPedestrian(world: World, pathIndex: number): PedestrianEntity {
  preloadNpcModels();
  const group = new THREE.Group();
  group.name = 'pedestrian';

  // Try to use a loaded GLTF model, otherwise fall back to primitives.
  if (loadedModels.length > 0) {
    const model = loadedModels[pathIndex % loadedModels.length]!.clone();
    // Scale to roughly 1 unit tall.
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const scale = 1.0 / Math.max(size.y, 0.01);
    model.scale.setScalar(scale);
    model.position.y = -box.min.y * scale;
    group.add(model);
  } else {
    // Primitive fallback.
    const colorHex = NPC_COLORS[pathIndex % NPC_COLORS.length]!;
    const mat = new THREE.MeshToonMaterial({ color: colorHex });
    const body = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.2, 0.7, 8), mat);
    body.position.y = 0.35; body.castShadow = true;
    group.add(body);
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.12, 8, 6), mat);
    head.position.y = 0.82; head.castShadow = true;
    group.add(head);
  }

  // Brainrot name tag.
  const npcName = BRAINROT_NAMES[pathIndex % BRAINROT_NAMES.length]!;
  const nameCanvas = document.createElement('canvas');
  nameCanvas.width = 256; nameCanvas.height = 32;
  const nCtx = nameCanvas.getContext('2d')!;
  nCtx.fillStyle = '#ffffff';
  nCtx.font = 'bold 14px Arial';
  nCtx.textAlign = 'center';
  nCtx.fillText(npcName, 128, 22);
  const nameSprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(nameCanvas), transparent: true }),
  );
  nameSprite.position.y = 1.4;
  nameSprite.scale.set(2.5, 0.3, 1);
  group.add(nameSprite);

  // Position at first waypoint.
  const path = sidewalkPaths[pathIndex];
  if (path && path[0]) group.position.copy(path[0]);
  world.scene.add(group);

  return {
    group, state: 'walking', pathIndex, waypointIndex: 0,
    stateTimer: 0, fleeDirection: new THREE.Vector3(),
    ragdollHorizVel: new THREE.Vector3(),
    ragdollVelY: 0,
    ragdollAngVel: new THREE.Vector3(),
    ragdollBounced: false,
  };
}
